import type { Ref } from 'react';
import { Box, Typography } from '@mui/material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlined from '@mui/icons-material/LocalPhoneOutlined';

export interface ContactInfoFieldProps {
  /** Group heading (e.g. "How we'll reach you"). */
  title: string;
  /** Contact email from the requested-for user's profile. */
  email?: string | null;
  /** Contact phone from the requested-for user's profile. */
  phone?: string | null;
  emailLabel: string;
  phoneLabel: string;
  /** Explanatory caption (e.g. "From {name}'s profile; included on the ticket."). */
  note?: string;
  /** Shown when neither email nor phone is on file. */
  emptyText: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Read-only contact summary (the Polaris kit `ContactInfoField`, made truthful).
 * The submit contract carries **no** contact fields — the ConnectWise ticket
 * resolves contact name / phone / email from the requested-for user's **profile**
 * server-side (`TicketCreationService`), so an editable field would silently
 * discard input. This surfaces the profile contact that will be used, with a note
 * that it comes from the profile. (A per-request contact override is deferred —
 * see TD-252.)
 */
export function ContactInfoField({
  title,
  email,
  phone,
  emailLabel,
  phoneLabel,
  note,
  emptyText,
  ref,
}: ContactInfoFieldProps) {
  const hasContact = !!email || !!phone;

  return (
    <Box ref={ref}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          p: '12px 14px',
          borderRadius: 1,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {hasContact ? (
          <>
            {email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                  <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                    {emailLabel}:
                  </Box>
                  {email}
                </Typography>
              </Box>
            )}
            {phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalPhoneOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                  <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                    {phoneLabel}:
                  </Box>
                  {phone}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{emptyText}</Typography>
        )}
      </Box>
      {note && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>{note}</Typography>
      )}
    </Box>
  );
}
